import { useState, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Bug, Upload, X, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface BugReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  reportadoPor: string;
  clientePerfilId?: string | null;
}

const BugReportDialog = ({ open, onOpenChange, reportadoPor, clientePerfilId }: BugReportDialogProps) => {
  const [descricao, setDescricao] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles].slice(0, 10));
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveFile = (idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSubmit = async () => {
    if (descricao.trim().length < 10) {
      toast({ title: "Descrição deve ter ao menos 10 caracteres", variant: "destructive" });
      return;
    }

    setLoading(true);
    try {
      // Upload files first
      const anexos: { file_url: string; file_name: string }[] = [];
      for (const file of files) {
        const ext = file.name.split(".").pop() || "bin";
        const path = `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage.from("bug-reports").upload(path, file);
        if (upErr) {
          console.error("Upload error:", upErr);
          continue;
        }
        const { data: urlData } = supabase.storage.from("bug-reports").getPublicUrl(path);
        anexos.push({ file_url: urlData.publicUrl, file_name: file.name });
      }

      const { data, error } = await supabase.functions.invoke("report-bug", {
        body: {
          descricao: descricao.trim(),
          reportado_por: reportadoPor,
          cliente_perfil_id: clientePerfilId || null,
          anexos,
        },
      });

      if (error) throw error;

      toast({ title: "Bug reportado com sucesso! Obrigado." });
      setDescricao("");
      setFiles([]);
      onOpenChange(false);
    } catch (err: any) {
      toast({ title: err.message || "Erro ao reportar bug", variant: "destructive" });
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="tibia-border bg-card max-w-md" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="font-pixel text-xs text-primary flex items-center gap-2">
            <Bug className="w-4 h-4" />
            Reportar Bug
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            Descreva o problema encontrado com o máximo de detalhes possível.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <textarea
            value={descricao}
            onChange={(e) => setDescricao(e.target.value)}
            placeholder="Descreva o bug encontrado... (mín. 10 caracteres)"
            maxLength={2000}
            rows={5}
            className="tibia-input rounded-sm w-full text-sm resize-none"
          />

          {/* File upload area */}
          <div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="tibia-tab rounded-sm px-3 py-2 font-pixel text-[8px] flex items-center gap-1.5 w-full justify-center hover:text-primary transition-colors"
            >
              <Upload className="w-3.5 h-3.5" />
              ANEXAR ARQUIVOS ({files.length}/10)
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.txt,.log"
              onChange={handleAddFiles}
              className="hidden"
            />
          </div>

          {/* File list */}
          {files.length > 0 && (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-2 bg-secondary/30 rounded-sm px-2 py-1">
                  {f.type.startsWith("image/") && (
                    <img src={URL.createObjectURL(f)} alt="" className="w-8 h-8 object-cover rounded-sm border border-border shrink-0" />
                  )}
                  <span className="text-[10px] text-foreground/80 truncate flex-1">{f.name}</span>
                  <span className="text-[9px] text-muted-foreground whitespace-nowrap">
                    {(f.size / 1024).toFixed(0)} KB
                  </span>
                  <button onClick={() => handleRemoveFile(i)} className="text-destructive hover:text-destructive/80">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2 justify-end pt-1">
            <button
              onClick={() => onOpenChange(false)}
              className="tibia-tab rounded-sm px-4 py-1.5 font-pixel text-[8px]"
            >
              Cancelar
            </button>
            <button
              onClick={handleSubmit}
              disabled={descricao.trim().length < 10 || loading}
              className="tibia-btn rounded-sm px-4 py-1.5 font-pixel text-[8px] flex items-center gap-1 disabled:opacity-50"
            >
              {loading ? <><Loader2 className="w-3 h-3 animate-spin" /> Enviando...</> : "Enviar"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BugReportDialog;
